import React from 'react';
import functions from '../../../util/functions';

const StudentStatement = ({ statement, student, customRef }) => {
    const photo = student?.photo || '';
    const hasPhoto = typeof photo === 'string' && photo.trim() !== '';

    const initials = (() => {
        const name = student?.full_name || 'N/A';
        const parts = name.split(' ').filter(Boolean);
        if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
        return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
    })();

    return (
        <div ref={customRef} className="student-statement-print">
            {/* Header Area */}
            <div
                className="statement-header content-wrapper d-flex justify-content-between align-items-start mb-4"
                style={{ gap: '20px', flexWrap: 'nowrap' }}
            >
                {/* Left Header */}
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                        className="d-flex align-items-start"
                        style={{ gap: '18px', flexWrap: 'nowrap' }}
                    >
                        {/* Student Photo */}
                        <div
                            className="student-photo-box"
                            style={{
                                width: '95px',
                                height: '95px',
                                minWidth: '95px',
                                borderRadius: '16px',
                                overflow: 'hidden',
                                background: '#f1f5f9',
                                border: '1px solid #dbe1e8',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            {hasPhoto ? (
                                <img
                                    src={photo}
                                    alt={student?.full_name || 'Student'}
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                    }}
                                />
                            ) : (
                                <div
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '28px',
                                        fontWeight: 700,
                                        color: '#64748b',
                                        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                                    }}
                                >
                                    {initials}
                                </div>
                            )}
                        </div>

                        {/* Student Info */}
                        <div style={{ flex: 1, minWidth: '260px' }}>
                            <h4 style={{ marginBottom: '14px' }}>Fees Statement</h4>

                            <div style={{ lineHeight: '1.8' }}>
                                <div>
                                    <strong>Student Name:</strong> {student?.full_name || 'N/A'}
                                </div>
                                <div>
                                    <strong>Class:</strong> {student?.class?.class_name || 'N/A'}
                                </div>
                                <div>
                                    <strong>Reg No.:</strong> {student?.reg_no || 'N/A'}
                                </div>
                                <div>
                                    <strong>Payment Code:</strong> {student?.payment_code || 'N/A'}
                                </div>
                            </div>

                            {functions.isObject(statement) && statement.summary && (
                                <div style={{ marginTop: '28px' }}>
                                    <strong>Statement Period:</strong>{' '}
                                    {statement.summary.opening_date ? (
                                        <>
                                            {functions.formatDate(statement.summary.opening_date)} -{' '}
                                            {functions.formatDate(statement.summary.closing_date)}
                                        </>
                                    ) : (
                                        'N/A'
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Right Summary */}
                {functions.isObject(statement) && statement.summary && (
                    <div
                        className="summary-table"
                        style={{ minWidth: '320px', maxWidth: '360px', width: '100%' }}
                    >
                        <table className="table table-bordered mb-0" style={{ fontSize: '12px' }}>
                            <tbody>
                                <tr>
                                    <th>Opening Balance</th>
                                    <td>{functions.numberFormat(statement.summary?.opening_balance || 0)}</td>
                                </tr>
                                <tr>
                                    <th>Invoices</th>
                                    <td>{functions.numberFormat(statement.summary?.total_billed || 0)}</td>
                                </tr>
                                <tr>
                                    <th>Payments</th>
                                    <td>{functions.numberFormat(statement.summary?.total_paid || 0)}</td>
                                </tr>
                                {statement.summary?.total_discounted > 0 && (
                                    <tr>
                                        <th>Discounts</th>
                                        <td>{functions.numberFormat(statement.summary?.total_discounted || 0)}</td>
                                    </tr>
                                )}
                                <tr>
                                    <th>Closing Balance</th>
                                    <td>{functions.numberFormat(statement.summary?.closing_balance || 0)}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Transactions Table */}
            <div className="card mb-4 transactions-card">
                <div className="card-body">
                    {functions.isObject(statement) && (
                        <table className="table table-striped table-bordered transactions-table">
                            <thead className="thead-light">
                                <tr>
                                    <th style={{ textWrap: 'nowrap' }}>Trans. Date</th>
                                    <th>Batch</th>
                                    <th>Description</th>
                                    <th>Invoice</th>
                                    <th>Payment</th>
                                    <th>Balance</th>
                                </tr>
                            </thead>
                            <tbody>
                                {Array.isArray(statement.transactions) && statement.transactions.length > 0 ? (
                                    statement.transactions.map((transaction, index) => (
                                        <tr key={index}>
                                            <td style={{ textWrap: 'nowrap' }}>
                                                {functions.formatDate(transaction.transaction_date)}
                                            </td>
                                            <td>{transaction.batch?.batch_name || ''}</td>
                                            <td>{transaction.description || '-'}</td>
                                            <td>
                                                {transaction.invoice > 0
                                                    ? functions.numberFormat(transaction.invoice)
                                                    : ''}
                                            </td>
                                            <td>
                                                {transaction.settlement > 0
                                                    ? functions.numberFormat(transaction.settlement)
                                                    : ''}
                                            </td>
                                            <td>{functions.numberFormat(transaction.running_balance)}</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="6" className="text-center">
                                            No transactions available for this period.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
};

export default StudentStatement;